import type { ModalProps, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useEffect, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { downloadBase64File, downloadFileByUrl } from 'helpers/utils';
import { PagedList } from 'interfaces/general';
import { FileDetail } from 'api/fileStorage';

const buttonSx: SxProps<Theme> = (theme) => ({
  backgroundColor: 'transparent',
  color: 'white',
  fontSize: theme.typography.fontSize * 1.15
});

type Props = {
  imageProps: React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  >;
  filename?: string;
  modalProps?: Omit<ModalProps, 'onClose' | 'open'>;
  open: boolean;
  imgContainerStyles?: any;
  onClose?: () => void;
  filePath?: FilePath;
  imageData?: any[];
  dataImages?: PagedList<FileDetail>;
  setOpenModalImage?: any;
};

type FilePath = {
  selectedImage: string;
  src: string;
  open: boolean;
};

const commmonSx = {
  transform: {
    xs: 'scale(1)',
    sm: 'scale(2)'
  },
  position: 'absolute',
  ':hover': {
    cursor: 'pointer'
  }
};

export function ModalImage({
  modalProps,
  onClose,
  filename,
  open,
  imgContainerStyles,
  filePath,
  imageData,
  dataImages,
  setOpenModalImage
}: Props) {
  const downloadFileHandler = () => {
    false &&
      downloadBase64File(
        `data:@file/png;base64,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`,
        filename
      );
    true && downloadFileByUrl(changeImageState, changeImageName);
  };

  imgContainerStyles = imgContainerStyles || { justifyContent: 'center' };

  const [changeImageState, setChangeImageState] = useState<string>(undefined);

  const [changeImageName, setChangeImageName] = useState(filename);

  useEffect(() => {
    const validateName = dataImages?.items?.find(
      (dt) => dt.blobStorageUrlPath === filePath.selectedImage
    );
    setChangeImageName(
      validateName?.fileName ||
        validateName?.ticketFileName ||
        'IMAGEN SIN NOMBRE'
    );
    setChangeImageState(filePath?.selectedImage);
    return () => {
      setChangeImageState(undefined);
    };
  }, [filePath?.selectedImage, dataImages?.items]);

  const handleOnClick = (direction: 'back' | 'foward') => {
    if (imageData.length && !!filePath?.selectedImage) {
      imageData?.forEach((dt, index) => {
        if (filePath.selectedImage === dt.src) {
          const nextORPrev = direction === 'foward' ? index + 1 : index - 1;
          const imageTOChange =
            imageData[nextORPrev > imageData.length - 1 ? 0 : nextORPrev]?.src;
          setChangeImageState(imageTOChange);
          setOpenModalImage((prev: any) => ({
            ...prev,
            selectedImage: imageTOChange
          }));
        }
      });
    }
  };

  const closeClickHandler = () => {
    setChangeImageName(null);
    onClose();
  };

  return (
    <Modal {...modalProps} open={open}>
      <Box
        className="box"
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <TransformWrapper initialScale={1} minScale={0.5}>
          {({ zoomIn, zoomOut, resetTransform }) => (
            <Stack
              sx={{
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {imageData[0]?.src === filePath.selectedImage ? (
                <></>
              ) : (
                <ArrowBackIcon
                  onClick={() => {
                    handleOnClick('back');
                    resetTransform();
                  }}
                  sx={{
                    left: {
                      xs: '30px',
                      sm: '70px',
                      md: '150px'
                    },
                    color: 'white',
                    ...commmonSx
                  }}
                />
              )}
              <Stack
                alignItems="center"
                width="100%"
                bgcolor="black"
                direction="row"
                height="auto"
                padding="5px"
                justifyContent="space-between"
              >
                <Typography
                  sx={{
                    wordBreak: 'break-all',
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  color="white"
                >
                  {changeImageName || ''}
                </Typography>
                <Stack direction="row" justifyContent="flex-end">
                  <Button onClick={downloadFileHandler} sx={buttonSx}>
                    <FileDownloadIcon />
                  </Button>
                  <Button onClick={() => zoomIn()} sx={buttonSx}>
                    <ZoomInIcon />
                  </Button>
                  <Button onClick={() => zoomOut()} sx={buttonSx}>
                    <ZoomOutIcon />
                  </Button>
                  <Button onClick={closeClickHandler} sx={buttonSx}>
                    <CloseIcon />
                  </Button>
                </Stack>
              </Stack>

              <TransformComponent contentStyle={imgContainerStyles}>
                <img
                  loading="lazy"
                  src={
                    !changeImageState
                      ? filePath?.selectedImage
                      : changeImageState
                  }
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </TransformComponent>
              {imageData.length > 1 && (
                <ArrowForwardIcon
                  onClick={() => {
                    handleOnClick('foward');
                    resetTransform();
                  }}
                  sx={{
                    right: {
                      xs: '30px',
                      sm: '70px',
                      md: '150px'
                    },
                    color: 'white',
                    ...commmonSx
                  }}
                />
              )}
            </Stack>
          )}
        </TransformWrapper>
      </Box>
    </Modal>
  );
}
export default ModalImage;
